import { Suspense, useState, forwardRef, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTileFilters } from '@/catalog-apps/hooks/useTileFilters'
import { useSubmitPageFilters } from '@/catalog-apps/components/AppPage2/Filter/useSubmitPageFilters'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { Button, type ButtonSize, type ButtonVariant } from '@/components/shadcn/button'
import { SheetTrigger } from '@/components/shadcn/sheet'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { CATALOG_BASE_PATH } from '@/const'
import { useSubmitForm } from '../FormComponent/useSubmitForm'
import { useCatalogComponentRuntime } from '../shared/CatalogComponentRuntimeContext'
import { SheetContentAction } from '../DataTableV2/components/SheetContentAction'
import { DataTableV2DataRow, DataTableV2SheetContentAction } from '../DataTableV2/types'
import { TemplateActivityLog } from '../DataTableComponent/RowActions/TemplateActivityLog'
import { UploadFilesActionPanel } from '../shared/UploadFilesActionPanel'
import { CollaborationContent } from '../DataTableV2/components/CollaborationMenuItemV2/CollaborationSheetContent'
import { FormComponentLoading } from '../FormComponent'
import {
  ButtonActionsAction,
  ButtonActionsCollaborationAction,
  ButtonActionsUploadFilesAction,
  ButtonActionsUploadTemplateAction,
} from './types'

const ICON_SIZES = new Set<NonNullable<ButtonSize>>(['icon', 'icon-xs', 'icon-sm', 'icon-lg'])

function isIconSize(size: NonNullable<ButtonSize>) {
  return ICON_SIZES.has(size)
}

function getActionTooltip(action: ButtonActionsAction) {
  const size = (action.size ?? 'default') as NonNullable<ButtonSize>
  return action.tooltip ?? (isIconSize(size) ? action.label : undefined)
}

const ActionButton = forwardRef<
  HTMLButtonElement,
  { action: ButtonActionsAction; onClick?: () => void }
>(function ActionButton({ action, onClick }, ref) {
  const variant = (action.variant ?? 'default') as ButtonVariant
  const size = (action.size ?? 'default') as NonNullable<ButtonSize>
  const iconOnly = isIconSize(size)
  return (
    <Button
      ref={ref}
      type="button"
      variant={variant}
      size={size}
      disabled={action.isDisabled}
      onClick={onClick}
    >
      {action.icon && <DynamicIcon iconName={action.icon} lazyLoad={false} />}
      {iconOnly ? <span className="sr-only">{action.label}</span> : action.label}
    </Button>
  )
})

function withTooltip(action: ButtonActionsAction, node: ReactNode) {
  const tooltip = getActionTooltip(action)
  if (!tooltip) return node
  return <TooltipButton tooltip={tooltip}>{node}</TooltipButton>
}

function SubmitButtonActionsAction({ action }: { action: Extract<ButtonActionsAction, { type: 'submit' }> }) {
  const { handlerId, id: actionId } = action
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm } = useSubmitForm(handlerId, [...tileFilterSelections, { actionId }], actionId)

  return withTooltip(
    action,
    <ActionButton action={action} onClick={() => submitForm(action.submitData ?? {})} />
  )
}

function SubmitButtonActionsActionWithConfirmation({
  action,
}: {
  action: Extract<ButtonActionsAction, { type: 'submit' }>
}) {
  const [isOpen, setIsOpen] = useState(false)
  const { handlerId, id: actionId, confirmationMessage, label } = action
  const { tileFilterSelections } = useTileFilters()
  const { mutate: submitForm, isPending: isSubmitting } = useSubmitForm(
    handlerId,
    [...tileFilterSelections, { actionId }],
    actionId,
    { onSuccess: () => setIsOpen(false) }
  )

  return (
    <ConfirmDialog
      trigger={<ActionButton action={action} />}
      triggerTooltip={getActionTooltip(action)}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      title={label}
      description={confirmationMessage}
      onConfirm={() => submitForm(action.submitData ?? {})}
      closeOnConfirm={false}
      isSubmitting={isSubmitting}
    />
  )
}

function NavigateButtonActionsAction({ action }: { action: Extract<ButtonActionsAction, { type: 'navigate' }> }) {
  const { submitFilters } = useSubmitPageFilters()
  const [searchParams] = useSearchParams()
  const { allowRouteNavigation = true } = useCatalogComponentRuntime()
  if (!allowRouteNavigation) {
    return withTooltip(action, <ActionButton action={action} />)
  }

  return withTooltip(
    action,
    <Link
      className="text-foreground"
      to={`${CATALOG_BASE_PATH}${action.pageId}?${searchParams.toString()}`}
      onClick={() => {
        if (action.pageFilters) {
          submitFilters(action.pageFilters)
        }
      }}
    >
      <ActionButton action={action} />
    </Link>
  )
}

function UploadTemplateButtonActionsAction({ action }: { action: ButtonActionsUploadTemplateAction }) {
  const [sheetOpen, setSheetOpen] = useState(false)

  return (
    <SheetWithFormComponent
      title="Upload template"
      description="Upload a template for the entity."
      trigger={withTooltip(action, <ActionButton action={action} />)}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <TemplateActivityLog entityGuid={action.entityGUID} templateKey={action.templateKey} />
    </SheetWithFormComponent>
  )
}

function UploadFilesButtonActionsAction({ action }: { action: ButtonActionsUploadFilesAction }) {
  const uploadNamespace = action.namespace.trim()
  return (
    <UploadFilesActionPanel
      action={action}
      title={action.label}
      uploadNamespace={uploadNamespace}
      permissions={action.permissions}
      trigger={(onOpen) => withTooltip(action, <ActionButton action={action} onClick={onOpen} />)}
    />
  )
}

function CollaborationButtonActionsAction({ action }: { action: ButtonActionsCollaborationAction }) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const { appId, workspaceId } = useAppContext()

  if (!appId || !workspaceId) return null

  return (
    <SheetWithFormComponent
      title={action.label}
      trigger={withTooltip(action, <ActionButton action={action} />)}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <QueryErrorBoundary errorMessage="Failed to fetch channel thread details">
        <Suspense fallback={<FormComponentLoading />}>
          <CollaborationContent
            channelKey={action.channelKey}
            threadKey={action.threadKey}
            applicationId={appId}
            workspaceId={workspaceId}
          />
        </Suspense>
      </QueryErrorBoundary>
    </SheetWithFormComponent>
  )
}

export function ButtonActionButton({ action }: { action: ButtonActionsAction }) {
  if (action.isVisible === false) return null

  switch (action.type) {
    case 'submit':
      return action.requireConfirmation ? (
        <SubmitButtonActionsActionWithConfirmation action={action} />
      ) : (
        <SubmitButtonActionsAction action={action} />
      )
    case 'navigate':
      return <NavigateButtonActionsAction action={action} />
    case 'sheet-content': {
      const trigger = withTooltip(
        action,
        <SheetTrigger asChild>
          <ActionButton action={action} />
        </SheetTrigger>
      )
      return (
        <SheetContentAction
          action={action as DataTableV2SheetContentAction}
          trigger={trigger}
          formData={action.sheetData as DataTableV2DataRow | undefined}
          isDisabled={action.isDisabled}
        />
      )
    }
    case 'upload-template':
      return <UploadTemplateButtonActionsAction action={action} />
    case 'upload-files':
      return <UploadFilesButtonActionsAction action={action} />
    case 'collaboration':
      return <CollaborationButtonActionsAction action={action} />
    default:
      return null
  }
}
